import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, RotateCcw, Sparkles, Pencil, Eye, Save, X, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * Side-by-side gallery of every candidate-facing email template, rendered
 * with the recruiter's current brand. Includes a "Normal ↔ White-label"
 * toggle plus an "Edit body" mode that lets the recruiter customise the
 * body paragraphs (using {name}, {position}, {employer}, {ref}, {link},
 * {status_link}, {withdraw_link} tokens) and save them via PATCH /auth/me.
 */
export default function EmailPreviewGallery({ open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState("invite");
  const [whitelabelOverride, setWhitelabelOverride] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [testSending, setTestSending] = useState(false);

  const load = async (override) => {
    setLoading(true);
    try {
      const params = override === null ? {} : { whitelabel: override };
      const { data: d } = await api.get("/email-templates/preview-all", {
        params,
      });
      setData(d);
    } catch {
      /* axios interceptor toasts */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load(whitelabelOverride);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, whitelabelOverride]);

  const activeTemplate = useMemo(
    () => data?.templates?.find((t) => t.kind === active),
    [data, active]
  );

  // Sync draft text when switching templates / fresh data.
  useEffect(() => {
    if (activeTemplate) {
      setDraft(activeTemplate.custom_body || "");
      setEditing(false);
    }
  }, [activeTemplate?.kind, activeTemplate?.custom_body]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveBody = async () => {
    if (!activeTemplate) return;
    setSaving(true);
    try {
      await api.patch("/auth/me", {
        [activeTemplate.field_name]: draft,
      });
      toast.success(
        draft.trim()
          ? `Saved custom ${activeTemplate.label} body`
          : `Reset ${activeTemplate.label} to default`
      );
      setEditing(false);
      await load(whitelabelOverride);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    setDraft("");
  };

  const cancelEdit = () => {
    setDraft(activeTemplate?.custom_body || "");
    setEditing(false);
  };

  const testSend = async () => {
    if (!activeTemplate) return;
    setTestSending(true);
    try {
      const payload = { kind: activeTemplate.kind };
      // Only send the inline draft when the editor is open AND it differs
      // from the saved custom_body — otherwise we just want the saved version.
      if (editing && draft !== (activeTemplate.custom_body || "")) {
        payload.body = draft;
      }
      const { data } = await api.post("/email-templates/test-send", payload);
      toast.success(`Test sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
          "Could not send test email (Resend may be unconfigured in this environment)"
      );
    } finally {
      setTestSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="email-preview-gallery"
        className="max-w-6xl p-0 h-[90vh] overflow-hidden flex flex-col"
      >
        <DialogHeader className="border-b border-slate-200 px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-blue-600" />
            Email preview gallery
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Sample-data renderings of every candidate-facing email. No real
            sends — safe to click through.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <aside className="w-56 flex-none border-r border-slate-200 bg-slate-50 p-2 overflow-y-auto">
            <div className="px-2 pb-2 pt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
              Templates
            </div>
            <nav className="flex flex-col gap-0.5">
              {(data?.templates || []).map((t) => (
                <button
                  key={t.kind}
                  type="button"
                  data-testid={`preview-tab-${t.kind}`}
                  onClick={() => setActive(t.kind)}
                  className={`text-left rounded-sm px-3 py-2 text-sm transition ${
                    active === t.kind
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:bg-white/60"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{t.label}</span>
                    {t.custom_body && (
                      <span
                        title="Customised"
                        className="ml-2 inline-flex h-1.5 w-1.5 flex-none rounded-full bg-blue-600"
                      />
                    )}
                  </div>
                  <div className="mt-0.5 text-[10px] text-slate-500 line-clamp-2">
                    {t.description}
                  </div>
                </button>
              ))}
            </nav>

            <div className="mt-6 border-t border-slate-200 pt-3 px-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                Brand override
              </div>
              <div
                data-testid="preview-whitelabel-toggle"
                className="mt-2 inline-flex items-center rounded-full border border-slate-300 bg-white p-1"
              >
                {[
                  ["saved", null, "Saved"],
                  ["normal", false, "Normal"],
                  ["whitelabel", true, "WL"],
                ].map(([key, val, label]) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`preview-wl-${key}`}
                    onClick={() => setWhitelabelOverride(val)}
                    className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] transition ${
                      whitelabelOverride === val
                        ? "bg-slate-900 text-white"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => load(whitelabelOverride)}
                disabled={loading}
                className="mt-2 h-7 w-full text-[11px]"
              >
                <RotateCcw className="mr-1.5 h-3 w-3" />
                Refresh
              </Button>
            </div>
          </aside>

          {/* Main pane */}
          <section className="flex flex-1 flex-col overflow-hidden bg-slate-100">
            {loading ? (
              <div className="flex flex-1 items-center justify-center text-slate-500">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : activeTemplate ? (
              <>
                <header className="border-b border-slate-200 bg-white px-6 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                      Subject
                    </div>
                    <div
                      data-testid="preview-subject"
                      className="mt-0.5 truncate text-sm font-semibold text-slate-900"
                    >
                      {activeTemplate.subject}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">
                      From:{" "}
                      <span className="font-medium text-slate-700">
                        {data?.brand?.label || "Hireloop PRO"}
                      </span>{" "}
                      · To:{" "}
                      <span className="font-mono">{data?.sample?.email}</span>
                    </div>
                  </div>
                  <div className="flex flex-none items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="preview-test-send-btn"
                      onClick={testSend}
                      disabled={testSending}
                      className="rounded-sm"
                      title="Send a test copy of this email to your own inbox"
                    >
                      {testSending ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Send className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      Test send to me
                    </Button>
                    <Button
                      size="sm"
                      variant={editing ? "default" : "outline"}
                      data-testid="preview-edit-toggle"
                      onClick={() => setEditing((v) => !v)}
                      className="rounded-sm"
                    >
                      {editing ? (
                        <Eye className="mr-1.5 h-3.5 w-3.5" />
                      ) : (
                        <Pencil className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      {editing ? "Hide editor" : "Edit body"}
                    </Button>
                  </div>
                </header>

                <div className="flex flex-1 overflow-hidden">
                  {editing && (
                    <div className="flex w-[44%] flex-none flex-col border-r border-slate-200 bg-white">
                      <div className="border-b border-slate-100 bg-slate-50 px-4 py-2.5">
                        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700">
                          Custom body
                        </div>
                        <div className="mt-1 text-[11px] text-slate-500">
                          Replace the body paragraphs with your own copy. The header logo, action buttons, and footer stay the same. Use{" "}
                          {(activeTemplate.available_tokens || []).map((t) => (
                            <code
                              key={t}
                              className="mx-0.5 rounded-sm bg-slate-100 px-1 font-mono text-[10px] text-slate-700"
                            >
                              {t}
                            </code>
                          ))}{" "}
                          for live values. Leave blank to revert to the platform default.
                        </div>
                      </div>
                      <textarea
                        data-testid="preview-body-editor"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder={activeTemplate.default_body}
                        className="flex-1 resize-none border-0 bg-white p-4 font-mono text-[12px] leading-relaxed text-slate-900 focus:outline-none focus:ring-0"
                      />
                      <div className="flex items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-4 py-2.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid="preview-reset-btn"
                          onClick={resetToDefault}
                          disabled={saving || !draft}
                          className="text-rose-700 hover:bg-rose-50"
                        >
                          <RotateCcw className="mr-1.5 h-3 w-3" />
                          Reset
                        </Button>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid="preview-cancel-btn"
                            onClick={cancelEdit}
                            disabled={saving}
                          >
                            <X className="mr-1.5 h-3 w-3" />
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            data-testid="preview-save-btn"
                            onClick={saveBody}
                            disabled={saving}
                            className="bg-slate-900 text-white hover:bg-slate-800"
                          >
                            {saving ? (
                              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                            ) : (
                              <Save className="mr-1.5 h-3 w-3" />
                            )}
                            Save
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  <iframe
                    title="email-preview"
                    data-testid={`preview-iframe-${activeTemplate.kind}`}
                    srcDoc={activeTemplate.html}
                    sandbox=""
                    className="flex-1 bg-white"
                  />
                </div>
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center text-slate-400">
                Pick a template
              </div>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
